import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/hooks/useAuth";
import { getApiUrl } from "@/utils/api";
import { CompanyCategory } from "@/types";
import { companyAtom } from "@/store/company";
import { datesAtom } from "@/store/dates";
import { useAtom } from "jotai";
import { categoriesAtom } from "@/store/categories";
import { levelsAtom } from "@/store/levels";
import { intensityAtom } from "@/store/intensity";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

interface ApiResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface TransformedCategory {
  id: number;
  name: string;
  level: string;
}

export default function CustomReport() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [company] = useAtom(companyAtom);
  const [dates, setDates] = useAtom(datesAtom);
  const [categories, setCategories] = useAtom(categoriesAtom);
  const [levels, setLevels] = useAtom(levelsAtom);
  const [intensity, setIntensity] = useAtom(intensityAtom);

  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);

  // Reset all values on component mount
  useEffect(() => {
    setStartDate(undefined);
    setEndDate(undefined);
    setSelectedCategories([]);
    setIntensity({ value: null, unit: null });
    setDates(null);
    setCategories([]);
    setLevels([]);
  }, [setDates, setIntensity, setCategories, setLevels]);

  const { data: categoriesData, isLoading } = useQuery<
    ApiResponse<CompanyCategory>
  >({
    queryKey: ["company-categories", company?.id],
    queryFn: async () => {
      const urlParams = `company=${company?.id}&is_active=true`;

      const response = await fetch(
        getApiUrl(`company-categories/?${urlParams}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch categories");
      }
      return response.json();
    },
    enabled: !!token && !!company,
  });

  const categoriesByLevel = useMemo(() => {
    if (!categoriesData?.results) return { "1": [], "2": [] };

    const transformedCategories: TransformedCategory[] =
      categoriesData.results.map((cat: CompanyCategory) => ({
        id: Number(cat.category),
        name: cat.category_details.name,
        level: cat.category_details.level,
      }));

    return transformedCategories
      .filter((category) => category.level === "1" || category.level === "2")
      .reduce(
        (acc, category) => {
          if (!acc[category.level]) {
            acc[category.level] = [];
          }
          acc[category.level].push(category);
          return acc;
        },
        {} as Record<string, TransformedCategory[]>,
      );
  }, [categoriesData]);

  const handleCategoryChange = (categoryId: number, checked: boolean) => {
    if (checked) {
      setSelectedCategories((prev) => [...prev, categoryId]);
    } else {
      setSelectedCategories((prev) => prev.filter((id) => id !== categoryId));
    }
  };

  const handleSelectAllLevel = (level: string) => {
    const levelCategories = categoriesByLevel[level] || [];
    const levelCategoryIds = levelCategories.map((cat) => cat.id);

    const allSelected = levelCategoryIds.every((id) =>
      selectedCategories.includes(id),
    );

    if (allSelected) {
      setSelectedCategories((prev) =>
        prev.filter((id) => !levelCategoryIds.includes(id)),
      );
    } else {
      setSelectedCategories((prev) => [
        ...new Set([...prev, ...levelCategoryIds]),
      ]);
    }
  };

  const isGenerateDisabled =
    !startDate ||
    !endDate ||
    !intensity.value ||
    !intensity.unit ||
    selectedCategories.length === 0;

  const handleGenerateReport = () => {
    if (isGenerateDisabled) return;

    const allCategories = [
      ...(categoriesByLevel["1"] || []),
      ...(categoriesByLevel["2"] || []),
    ];

    const selectedCategoryDetails = allCategories.filter((cat) =>
      selectedCategories.includes(cat.id),
    );

    const uniqueLevels = [
      ...new Set(selectedCategoryDetails.map((cat) => parseInt(cat.level, 10))),
    ];

    setDates({
      fromDate: format(startDate, "yyyy-MM-dd"),
      toDate: format(endDate, "yyyy-MM-dd"),
    });
    setCategories(selectedCategories);
    setLevels(uniqueLevels);
    window.open("/report/estandard", "_blank");
  };

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold mb-2">
          {t("customReport.title")}
        </h1>
        <p className="text-muted-foreground mb-2">
          {t("customReport.description")}
        </p>
        <div className="text-md font-medium"> {company?.name}</div>
      </div>

      <div className="space-y-8">
        {/* Date Selection */}
        <div className="space-y-4">
          <h2 className="text-lg font-medium">{t("customReport.dateRange")}</h2>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2">
                {t("customReport.startDate")}
              </label>
              <DatePicker date={startDate} setDate={setStartDate} />
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium mb-2">
                {t("customReport.endDate")}
              </label>
              <DatePicker date={endDate} setDate={setEndDate} />
            </div>
          </div>
        </div>

        {/* Intensity Selection */}
        <div className="space-y-4">
          <h2 className="text-lg font-medium">{t("customReport.intensity")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="number"
              min="0"
              placeholder={t("customReport.intensityValue")}
              value={intensity.value ?? ""}
              onChange={(e) =>
                setIntensity({ ...intensity, value: Number(e.target.value) })
              }
              className="p-3 border rounded-lg"
            />
            <Select
              value={intensity.unit ?? undefined}
              onValueChange={(value) =>
                setIntensity({ ...intensity, unit: value })
              }
            >
              <SelectTrigger className="p-3 border rounded-lg">
                <SelectValue placeholder={t("customReport.intensityUnit")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="worker">
                  {t("customReport.workers")}
                </SelectItem>
                <SelectItem value="m2">{t("customReport.surface")}</SelectItem>
                <SelectItem value="office">
                  {t("customReport.offices")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Category Selection */}
        <div className="space-y-6">
          <h2 className="text-lg font-medium">
            {t("customReport.categories")}
          </h2>

          {isLoading ? (
            <div className="space-y-4">
              <div className="h-8 bg-gray-200 rounded animate-pulse" />
              <div className="h-8 bg-gray-200 rounded animate-pulse" />
            </div>
          ) : (
            <>
              {/* Alcance 1 */}
              {categoriesByLevel["1"]?.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      {t("customReport.alcance1")}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSelectAllLevel("1")}
                    >
                      {t("customReport.selectAll")}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {categoriesByLevel["1"].map((category) => (
                      <div
                        key={category.id}
                        className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50"
                      >
                        <Checkbox
                          id={`category-${category.id}`}
                          checked={selectedCategories.includes(category.id)}
                          onCheckedChange={(checked) =>
                            handleCategoryChange(
                              category.id,
                              checked as boolean,
                            )
                          }
                        />
                        <label
                          htmlFor={`category-${category.id}`}
                          className="flex-1 cursor-pointer"
                        >
                          {t(category.name)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Alcance 2 */}
              {categoriesByLevel["2"]?.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      {t("customReport.alcance2")}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSelectAllLevel("2")}
                    >
                      {t("customReport.selectAll")}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {categoriesByLevel["2"].map((category) => (
                      <div
                        key={category.id}
                        className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50"
                      >
                        <Checkbox
                          id={`category-${category.id}`}
                          checked={selectedCategories.includes(category.id)}
                          onCheckedChange={(checked) =>
                            handleCategoryChange(
                              category.id,
                              checked as boolean,
                            )
                          }
                        />
                        <label
                          htmlFor={`category-${category.id}`}
                          className="flex-1 cursor-pointer"
                        >
                          {t(category.name)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Generate Report Button */}
        <div className="flex justify-center pt-6">
          <Button
            onClick={handleGenerateReport}
            disabled={isGenerateDisabled}
            size="lg"
            className="px-8"
          >
            {t("customReport.generateReport")}
          </Button>
        </div>
      </div>
    </div>
  );
}
